import { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import { isChromeV2 } from '../lib/chromeV2';
import { orgApi } from '../lib/api';
import { useJiraLastSync, formatSyncTime } from '../hooks/useJiraLastSync';
import { useSettingsBadges } from '../hooks/useSettingsBadges';
import SettingsBadge from './settings/SettingsBadge';
import TierBadge from './TierBadge';
import ReportIssueModal from './ReportIssueModal';
import AviumLogo from './shared/AviumLogo';
import AddDataMenu from './AddDataMenu';
import SidebarAccountZone from './SidebarAccountZone';
import { STATUS_PAGE_URL } from '../lib/siteConfig';
import type { SettingsBadgeKey } from '../lib/settingsNav';
import { PRODUCT_SURFACES, TOUR_ANCHOR } from '../lib/surfaceRegistry';
// Nav icons live in the surface registry alongside route/label/pillar; the one
// icon rendered directly here is the Settings gear (not a product surface).
import { Settings } from 'lucide-react';

// The top-level nav derives from the product surface registry — the ONE place
// the IA order lives ([[ia-consolidation]]: answer → proof → tools, grouped by
// the See/Plan/Catch slogan). Reordering the product = reordering
// PRODUCT_SURFACES in lib/surfaceRegistry.ts; the guided tour's contract tests
// then force the tour storyboard to follow in the same change. History and
// per-surface rationale (Explore's demotion, 'Files' vs 'Documents', the
// dissolved Intelligence bucket) live in the registry's comments.
const navItems = PRODUCT_SURFACES
  .filter(s => s.sidebar)
  .map(s => ({ to: s.route, label: s.label, icon: s.icon }));

// Admin settings used to live here as two collapsibles (System Settings /
// System Config) of 15 links expanded in the global sidebar. As of the
// Settings-hub consolidation (2026-06-18) the global sidebar carries a single
// "Settings" entry -> /admin, which opens the grouped hub (SettingsHomePage)
// plus the in-area grouped rail (AdminLayout). The full link map now lives in
// lib/settingsNav.ts (one source of truth for the hub + the rail).

const navLinkClass = (isActive: boolean) =>
  `flex items-center gap-2 px-2 py-2 rounded text-sm mb-0.5 transition-colors ${
    isActive ? 'bg-brand-primary text-white' : 'text-white hover:bg-teal-800 hover:text-white'
  }`;

export default function Sidebar() {
  const { isAdmin, user, org, viewAsUser, toggleViewAsUser, isImpersonating, exitImpersonation } = useAuthStore();
  // Read once at render: the flag only changes via a reload (URL override writes
  // localStorage, then the app boots fresh), so there is nothing to subscribe to.
  const chromeV2 = isChromeV2();
  const navigate = useNavigate();
  const showAdmin = isAdmin();
  const jiraLastSync = useJiraLastSync();

  const [reportOpen, setReportOpen] = useState(false);
  // Footer "Help & resources" group — collapsed by default so the tour / docs /
  // status / report-issue entries don't eat vertical space.
  const [helpOpen, setHelpOpen] = useState(false);
  const [companyLogo, setCompanyLogo] = useState<string>('');

  // Settings attention counts — shared with the Settings home + in-area rail
  // (one hook owns the fetch + 20s poll + live event refresh). Gated on admin.
  const { unmappedCount, jiraModifiedCount } = useSettingsBadges({ enabled: showAdmin, poll: true });

  useEffect(() => {
    orgApi.branding().then(r => {
      const logo = r.data.find((c: { key: string; value: string }) => c.key === 'logo-url');
      setCompanyLogo(logo?.value || '');
    }).catch(() => {});

    const onUpdate = (e: Event) => setCompanyLogo((e as CustomEvent<string>).detail);
    window.addEventListener('branding-logo-updated', onUpdate);
    return () => window.removeEventListener('branding-logo-updated', onUpdate);
  }, []);

  // The single "Settings" entry surfaces an at-a-glance attention cue: the
  // orange "to push" count (tickets edited in Avium, not yet pushed to Jira)
  // takes precedence; otherwise an amber "!" for tickets sitting in no board
  // column (statusId = null). The precise per-item badges live inside the
  // Settings rail (AdminLayout); SettingsBadge owns the wording.
  const topBadge: SettingsBadgeKey | null =
    jiraModifiedCount > 0 ? 'jiraModified' : unmappedCount > 0 ? 'unmapped' : null;

  return (
    <aside className="w-56 bg-brand-primary-dark flex flex-col shrink-0">
      <div className="px-4 py-5 border-b border-teal-800">
        <div className="flex flex-col items-center text-center">
          <AviumLogo size={48} variant="dark" className="mb-2" />
          <div className="text-white font-brand font-extrabold text-2xl tracking-tight leading-none">Avium</div>
          {/* Pillar slogan removed from the sidebar 2026-06-27 at the user's
              request — the See/Plan/Catch tagline still lives on marketing
              surfaces (login page, docs footer, getavium.com). */}
          {/* The organization, under the logo — one-bar chrome only (Brian,
              2026-07-17, verbatim): "we need to move the organization name in
              this case merge somewhere on the side menu potentially somewhere
              underneath the avium logo and name I'm now realizing that this is
              not the best location in the Consolidated format."
              The org is IDENTITY, not a page tool: it was the only control in
              the one bar with nothing to do with the page you are looking at,
              so it sits where identity already lives. v1 keeps it in the green
              header — this is flag-gated, and nothing moves until the flag flips
              (Law 4: no silent relocation). */}
          {chromeV2 && org && (
            <div className="mt-2 text-teal-100 text-[10px] font-extrabold tracking-[0.08em] uppercase px-2.5 py-1 rounded-full bg-white/10 border border-white/15">
              {org.name}
            </div>
          )}
          <div className="mt-2.5">
            <TierBadge linkToBilling={showAdmin} />
          </div>
        </div>
        {companyLogo && (
          // Logo slot sized to handle both shapes well: a 4:1 banner logo
          // fills the width at ~h-20, and a 1:1 square logo lands at a clear
          // 80×80 (was 40×40 — too small to read when the customer's logo
          // happens to be a square mark / circular avatar).
          <div className="mt-4 pt-4 border-t border-teal-800">
            <div className="mx-auto flex items-center justify-center h-20 max-w-[180px]">
              <img src={companyLogo} alt="Company logo" className="max-h-full max-w-full object-contain opacity-90" />
            </div>
          </div>
        )}
      </div>

      {/* Clean-slate chrome (v2, approved 2026-07-18): the sidebar's primary
          create action, same menu as v1's Header button — see AddDataMenu's
          `variant` prop. v1 keeps "+ Add data" in the green header;
          nothing moves until the flag flips (Law 4: no silent relocation). */}
      {chromeV2 && (
        <div className="px-3 pt-3">
          <AddDataMenu variant="sidebar" />
        </div>
      )}

      {/* Impersonation banner */}
      {isImpersonating && (
        <div className="bg-red-600 px-3 py-2 text-white text-xs flex items-center justify-between">
          <span>🛡️ Support: {user?.name}</span>
          <button onClick={() => { exitImpersonation(); navigate('/super-admin'); }} className="underline hover:no-underline font-semibold">Exit</button>
        </div>
      )}

      {/* User view banner */}
      {viewAsUser && user?.role === 'admin' && !isImpersonating && (
        <div className="bg-brand-spark px-3 py-2 text-white text-xs flex items-center justify-between">
          <span>👤 User View</span>
          <button onClick={toggleViewAsUser} className="underline hover:no-underline">Exit</button>
        </div>
      )}

      <nav data-tour={TOUR_ANCHOR.sidebarNav} className="flex-1 py-4 overflow-y-auto">
        {/* Main nav */}
        <div className="px-3 mb-2">
          <p className="text-white text-xs font-semibold tracking-normal px-2 mb-1">Main</p>
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} className={({ isActive }) => navLinkClass(isActive)}>
              <item.icon size={16} strokeWidth={1.75} className="shrink-0" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>

        {/* Settings — a single entry that opens the grouped Settings hub
            (SettingsHomePage) + the in-area rail (AdminLayout). Replaced the old
            two-collapsible block of 15 links expanded in the sidebar. Visible to
            EVERY role since 2026-07-01: the hub is access-aware
            (settingsNavForRole) — admins get the full administration groups,
            everyone else gets "Your account" (profile, password, role,
            notifications), which previously hid behind the header profile
            dropdown with no sidebar presence at all. The data-tour anchor is
            admin-only so guided-tour stop 5 ("Admin customization & control")
            keeps targeting the admin affordance, exactly as before. */}
        <div data-tour={showAdmin ? TOUR_ANCHOR.adminCustomization : undefined} className="px-3 mt-4">
          <p className="text-white text-xs font-semibold tracking-normal px-2 mb-1">
            {showAdmin ? 'Administration' : 'Your account'}
          </p>
          <NavLink
            to="/admin"
            className={({ isActive }) => navLinkClass(isActive)}
          >
            <Settings size={16} strokeWidth={1.75} className="shrink-0" />
            <span className="flex-1">Settings</span>
            {topBadge && (
              <SettingsBadge
                badgeKey={topBadge}
                count={topBadge === 'jiraModified' ? jiraModifiedCount : unmappedCount}
                className="ml-1"
              />
            )}
          </NavLink>
        </div>
      </nav>

      {/* Support Console + Command Center moved into the account menu
          (shared/SuperAdminMenuItems, Brian 2026-07-19) — beside Notifications,
          Dark mode, and Change Password. The "jarvis" easter egg still works. */}

      <div className="border-t border-teal-800">
        {/* Jira sync — a live status shortcut, conditional, kept visible (not a
            "help" item). Clicking opens Data Sources to re-sync. */}
        {jiraLastSync && (
          <button
            onClick={() => navigate('/data')}
            title="Most recent live Jira sync — click to open Data Sources and re-sync"
            className="w-full flex items-center gap-2 px-4 py-2.5 text-white hover:text-white hover:bg-teal-800/40 transition-colors text-xs font-medium text-left"
          >
            <span>🔄</span>
            <span className="flex flex-col leading-tight">
              <span>Jira synced</span>
              <span className="text-teal-400 text-[10px]">{formatSyncTime(jiraLastSync)}</span>
            </span>
          </button>
        )}

        {/* Help & resources — collapsible (collapsed by default to save space).
            Groups the tour, product overview, docs, status, and issue reporting.
            The tour entries fire window events the Layout listens for (event-
            based): "Take the tour" → avium:open-guided-tour (interactive first-
            run walkthrough); "Product overview" → avium:open-tour (marketing
            carousel). They don't chain. Mirrors the Administration collapsible. */}
        <button
          type="button"
          onClick={() => setHelpOpen(o => !o)}
          className="w-full flex items-center justify-between px-4 py-3 text-xs font-medium text-white hover:text-white hover:bg-teal-800/40 transition-colors"
        >
          <span className="flex items-center gap-2"><span>🛟</span>Help &amp; resources</span>
          <span className={`text-white text-base leading-none transition-transform duration-200 ${helpOpen ? 'rotate-0' : '-rotate-90'}`}>▾</span>
        </button>

        {helpOpen && (
          <div className="pb-1">
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event('avium:open-guided-tour'))}
              className="w-full flex items-center gap-2 pl-8 pr-4 py-2 transition-colors text-xs font-medium text-left text-white hover:text-white hover:bg-teal-800/40"
            >
              <span>🧭</span>
              Take the tour
            </button>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event('avium:open-tour'))}
              className="w-full flex items-center gap-2 pl-8 pr-4 py-2 transition-colors text-xs font-medium text-left text-white hover:text-white hover:bg-teal-800/40"
            >
              <span>🎬</span>
              Product overview
            </button>
            <NavLink
              to="/docs?tab=architecture"
              className={({ isActive }) =>
                `w-full flex items-center gap-2 pl-8 pr-4 py-2 transition-colors text-xs font-medium text-left ${
                  isActive ? 'bg-teal-800 text-white' : 'text-white hover:text-white hover:bg-teal-800/40'
                }`
              }
            >
              <span>⚡</span>
              Documentation
            </NavLink>
            <a
              href={STATUS_PAGE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center gap-2 pl-8 pr-4 py-2 text-white hover:text-white hover:bg-teal-800/40 transition-colors text-xs font-medium text-left"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
              System Status
              <span className="ml-auto text-teal-400 text-[10px]">↗</span>
            </a>
            <button
              onClick={() => setReportOpen(true)}
              className="w-full flex items-center gap-2 pl-8 pr-4 py-2 text-white hover:text-white hover:bg-teal-800/40 transition-colors text-xs font-medium text-left"
            >
              <span>🐛</span>
              Report an Issue
            </button>
          </div>
        )}

        <ReportIssueModal open={reportOpen} onClose={() => setReportOpen(false)} />
        <div className="px-4 pb-4 pt-1 text-teal-400 text-xs">
          v2.4.0
        </div>
      </div>

      {/* Clean-slate chrome (v2, approved 2026-07-18): the account zone
          (Units · Upgrade · account block) that replaces GlobalControls'
          header/row rendering. v1 leaves the sidebar untouched. */}
      {chromeV2 && <SidebarAccountZone />}
    </aside>
  );
}
